import { memo } from 'react';
import { AppealListItem } from '../../types/appeals';
import { AppealStatusBadge } from './AppealStatusBadge';
import { AppealVersionBadge } from './AppealVersionBadge';
import { getV2Outcome, isV2Appeal, V2_OUTCOME_META } from '../../utils/appealsV2';
import { PlayerProfile } from '../PlayerProfile';
import { formatDistanceToNow } from 'date-fns';
import { Calendar, Globe, Server, Gavel, Clock, ArrowRight, Bot, UserPlus, FileText } from 'lucide-react';
import { cn } from '../../utils/cn';
import { decodeHtmlEntities } from '../../utils/decodeHtmlEntities';
import { Link } from 'react-router-dom';

interface AppealCardProps {
  appeal: AppealListItem;
}

type Phase = 'staff' | 'bot' | 'terminal';

const STATUS_PHASE: Record<string, Phase> = {
  pending: 'staff',
  awaiting_staff: 'staff',
  staff_answered: 'bot',
  in_jury: 'bot',
  jury_decided: 'bot',
  approved: 'terminal',
  denied: 'terminal',
  escalated: 'terminal',
  cancelled: 'terminal',
};

const PHASE_LABELS: Record<Phase, { text: string; color: string }> = {
  staff: { text: 'Staff Action', color: 'text-orange-400' },
  bot: { text: 'Mr.Roboto / Jury', color: 'text-purple-400' },
  terminal: { text: 'Closed', color: 'text-glass-500' },
};

const STATUS_ACTIONS: Record<string, string> = {
  pending: 'Needs staff assignment',
  awaiting_staff: 'Staff must answer 10 questions',
  staff_answered: 'Waiting for Mr.Roboto',
  in_jury: 'Jury deliberating via Mr.Roboto',
  jury_decided: 'Mr.Roboto finalizing verdict',
  approved: 'Ban overturned',
  denied: 'Ban upheld',
  escalated: 'Escalated for senior review',
  cancelled: 'Appeal cancelled',
};

const ScopeIcon = ({ scope }: { scope: string }) => {
  switch (scope) {
    case 'global': return <Globe className="w-3.5 h-3.5" />;
    case 'universal': return <Globe className="w-3.5 h-3.5 text-purple-400" />;
    default: return <Server className="w-3.5 h-3.5" />;
  }
};

const V2_STATUS_ACTIONS: Record<string, string> = {
  in_jury_stage_1: 'Jury Stage 1 — uphold or vary?',
  in_jury_stage_2: 'Jury Stage 2 — choosing variation',
  upheld: 'Ban upheld by jury',
  removed: 'Ban removed',
  reduced: 'Ban reduced',
  increased: 'Ban extended',
  made_unappealable: 'Ban made unappealable',
};

export const AppealCard = memo(({ appeal }: AppealCardProps) => {
  const banReasonDisplay = decodeHtmlEntities(appeal.ban_reason);
  const isPermanentBan = appeal.ban_expires === null;
  const isBanExpired = !isPermanentBan && new Date((appeal.ban_expires || 0) * 1000) < new Date();
  const phase = STATUS_PHASE[appeal.status] || 'terminal';
  const phaseLabel = PHASE_LABELS[phase];

  const isV2 = isV2Appeal(appeal);
  const v2Outcome = isV2 ? getV2Outcome(appeal.v2_stage1_result, appeal.v2_stage2_result) : null;
  let actionHint = STATUS_ACTIONS[appeal.status] || '';
  if (isV2) {
    if (appeal.status === 'in_jury') {
      actionHint = V2_STATUS_ACTIONS[appeal.v2_jury_stage === 2 ? 'in_jury_stage_2' : 'in_jury_stage_1'];
    } else if (v2Outcome && (appeal.status === 'approved' || appeal.status === 'denied')) {
      actionHint = V2_STATUS_ACTIONS[v2Outcome] || actionHint;
    }
  }

  return (
    <Link
      to={`/appeals/${appeal.id}`}
      className={cn(
        'glass-card glass-card-interactive p-4 space-y-3 group block',
        phase === 'staff' && appeal.status === 'pending' && 'border-yellow-500/30',
        phase === 'staff' && appeal.status === 'awaiting_staff' && 'border-orange-500/30',
        phase === 'bot' && 'border-purple-500/20',
        appeal.status === 'escalated' && 'border-amber-500/30',
      )}
    >
      <div className="flex flex-wrap justify-between items-start gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <span className={cn(
            'icon-tile',
            appeal.status === 'pending' ? 'icon-tile-warning' :
            appeal.status === 'awaiting_staff' ? 'icon-tile-orange' :
            phase === 'bot' ? 'icon-tile-purple' :
            appeal.status === 'approved' ? 'icon-tile-success' :
            appeal.status === 'denied' ? 'icon-tile-danger' :
            'icon-tile-neutral'
          )}>
            <Gavel className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-white tabular-nums">Appeal #{appeal.id}</span>
              <span className="text-xs text-glass-400 tabular-nums">for Ban #{appeal.ban_id}</span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <PlayerProfile uuid={appeal.user_uuid} name={appeal.username} inline showUuid={false} showAvatar />
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <AppealVersionBadge appeal={appeal} />
            <AppealStatusBadge status={appeal.status} />
            <ArrowRight className="w-4 h-4 text-glass-400 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <div className="flex items-center gap-1">
            {phase === 'bot' && <Bot className="w-3 h-3 text-purple-400" />}
            {phase === 'staff' && appeal.status === 'pending' && <UserPlus className="w-3 h-3 text-yellow-400" />}
            {phase === 'staff' && appeal.status === 'awaiting_staff' && <FileText className="w-3 h-3 text-orange-400" />}
            <span className={cn('text-xs font-medium', phaseLabel.color)}>{phaseLabel.text}</span>
          </div>
        </div>
      </div>

      <p className="text-sm text-glass-300 subcard px-2.5 py-2 truncate" title={banReasonDisplay}>
        {banReasonDisplay}
      </p>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-glass-400">
        <div className="flex items-center gap-1" title="What needs to happen next">
          <span className={cn('font-medium', phaseLabel.color)}>{actionHint}</span>
        </div>
        <span className="text-glass-200/20">|</span>
        <div className="flex items-center gap-1 tabular-nums" title="Submitted">
          <Clock className="w-3.5 h-3.5" />
          <span>{formatDistanceToNow(new Date(appeal.created_at), { addSuffix: true })}</span>
        </div>
        <div className="flex items-center gap-1" title="Ban scope">
          <ScopeIcon scope={appeal.ban_scope} />
          <span className="capitalize">{appeal.ban_scope}{appeal.ban_scope_target ? ` (${appeal.ban_scope_target})` : ''}</span>
        </div>
        <div className="flex items-center gap-1 tabular-nums" title="Ban duration">
          <Calendar className="w-3.5 h-3.5" />
          <span>{isPermanentBan ? 'Permanent' : isBanExpired ? 'Ban Expired' : `Expires ${formatDistanceToNow(new Date(appeal.ban_expires! * 1000), { addSuffix: true })}`}</span>
        </div>
        {appeal.assigned_staff_id && (
          <div className="flex items-center gap-1" title="Assigned to">
            <span className="text-accent-300">Assigned:</span>
            <PlayerProfile uuid={appeal.assigned_staff_id} inline showUuid={false} />
          </div>
        )}
        {appeal.verdict && (
          <span className={cn(
            'font-semibold',
            appeal.verdict === 'overturned' ? 'text-green-400' : 'text-red-400'
          )}>
            Verdict: {appeal.verdict}
          </span>
        )}
        {v2Outcome && (
          <span className={cn('font-semibold', V2_OUTCOME_META[v2Outcome].text)}>
            Outcome: {V2_OUTCOME_META[v2Outcome].label}
          </span>
        )}
      </div>
    </Link>
  );
});
